/**
 * m3 echo Frontend - Admin Dashboard
 *
 * Unified admin page with tabbed interface for all admin functionality.
 */

import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useUIStore } from '../../stores/useUIStore';

// Tab components
import TenantManagement from './TenantManagement';
import UserManagement from './UserManagement';
import CostReporting from './CostReporting';
import ModelsTab from '../../components/admin/ModelsTab';
import TokensTab from '../../components/admin/TokensTab';
import LayersTab from '../../components/admin/LayersTab';
import SystemTab from '../../components/admin/SystemTab';
import ChangelogTab from '../../components/admin/ChangelogTab';
import QueueTab from '../../components/admin/QueueTab';
import AutopilotTab from '../../components/admin/AutopilotTab';
import LogsTab from '../../components/admin/LogsTab';
import CheatsheetTab from '../../components/admin/CheatsheetTab';
import OpsTab from '../../components/admin/OpsTab';
import AuditTab from '../../components/admin/AuditTab';
import AnimatedTabPanel from '../../components/AnimatedTabPanel';

const tabs = [
  { label: 'Ops', key: 'ops' },
  { label: 'Mandanten', key: 'tenants' },
  { label: 'Nutzer', key: 'users' },
  { label: 'Modellpflege', key: 'models' },
  { label: 'Kostenreports', key: 'costs' },
  { label: 'Tokens', key: 'tokens' },
  { label: 'Layers', key: 'layers' },
  { label: 'System', key: 'system' },
  { label: 'Changelog', key: 'changelog' },
  { label: 'Queue', key: 'queue' },
  { label: 'Autopilot', key: 'autopilot' },
  { label: 'Logs', key: 'logs' },
  { label: 'Cheatsheet', key: 'cheatsheet' },
  { label: 'Audit-Protokoll', key: 'audit' },
];

export const AdminDashboard: React.FC = () => {
  const { adminActiveTab, setAdminTab } = useUIStore();

  // Bounds check for persisted tab state
  useEffect(() => {
    if (adminActiveTab >= tabs.length) {
      setAdminTab(0);
    }
  }, [adminActiveTab, setAdminTab]);

  // Ensure we don't render with invalid tab index
  const safeTabIndex = adminActiveTab < tabs.length ? adminActiveTab : 0;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        Admin
      </Typography>

      <Paper>
        <Tabs
          value={safeTabIndex}
          onChange={(_, newValue) => setAdminTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>

        {/* Ops Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={0}>
          <OpsTab />
        </AnimatedTabPanel>

        {/* Mandanten Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={1}>
          <TenantManagement embedded />
        </AnimatedTabPanel>

        {/* Nutzer Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={2}>
          <UserManagement embedded />
        </AnimatedTabPanel>

        {/* Modellpflege Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={3}>
          <ModelsTab />
        </AnimatedTabPanel>

        {/* Kostenreports Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={4}>
          <CostReporting embedded />
        </AnimatedTabPanel>

        {/* Tokens Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={5}>
          <TokensTab />
        </AnimatedTabPanel>

        {/* Layers Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={6}>
          <LayersTab />
        </AnimatedTabPanel>

        {/* System Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={7}>
          <SystemTab />
        </AnimatedTabPanel>

        {/* Changelog Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={8}>
          <ChangelogTab />
        </AnimatedTabPanel>

        {/* Queue Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={9}>
          <QueueTab />
        </AnimatedTabPanel>

        {/* Autopilot Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={10}>
          <AutopilotTab />
        </AnimatedTabPanel>

        {/* Logs Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={11}>
          <LogsTab />
        </AnimatedTabPanel>

        {/* Cheatsheet Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={12}>
          <CheatsheetTab />
        </AnimatedTabPanel>

        {/* Audit-Protokoll Tab */}
        <AnimatedTabPanel value={safeTabIndex} index={13}>
          <AuditTab />
        </AnimatedTabPanel>
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
